import {
  Component,
  EventEmitter,
  Output,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import {
  WidgetActionsService,
  WidgetMenuItem,
} from '../../services/widget-actions.service';
import { NomObjectClass } from '../../constants';
import { selectCurrentObject } from '../../reducers';
import { AppState } from 'src/app/reducers';

/**
 * Widget toolbar shared by the dashboard and feed hosts. Delegates domain actions
 * to `WidgetActionsService`; per-host behaviours (search/refresh/configure/select-all)
 * are emitted as outputs.
 */
@Component({
  selector: 'app-widget-toolbar',
  templateUrl: './widget-toolbar.component.html',
  styleUrl: './widget-toolbar.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WidgetToolbarComponent {
  @Output() searchChange = new EventEmitter<string>();
  @Output() refresh = new EventEmitter<void>();
  @Output() configure = new EventEmitter<void>();
  @Output() selectAllChange = new EventEmitter<boolean>();

  private store = inject(Store<AppState>);
  private translate = inject(TranslateService);
  readonly was = inject(WidgetActionsService);

  selectAllChecked = false;
  isDashboard = false;

  readonly selectedCount$ = this.was.selectedCount$;
  readonly viewItems$ = this.was.viewItems$;
  readonly languageItems$ = this.was.languageItems$;
  // The command lists are static (they only branch on selected-vs-all mode).
  readonly actionItems: WidgetMenuItem[] = this.was.getWidgetActionItems(false);
  readonly selectedItems: WidgetMenuItem[] =
    this.was.getWidgetActionItems(true);

  get actionsLabel(): string {
    return this.translate.instant('widget-actions.button.actions');
  }
  get selectedLabel(): string {
    return this.translate.instant('widget-actions.button.selected');
  }
  get viewLabel(): string {
    return this.translate.instant('widget-actions.button.view');
  }
  get languagesLabel(): string {
    return this.translate.instant('widget-actions.button.languages');
  }

  /** Selected value of a mutually exclusive row set, for a `sui-radio-group` binding. */
  selectedValueOf(items: WidgetMenuItem[]): unknown {
    return items.find((item) => item.isSelected)?.value;
  }

  /** The group owns the selection; delegate to the picked row's own action. */
  onLanguageChange(items: WidgetMenuItem[], value: unknown): void {
    items.find((item) => item.value === value)?.action?.();
  }

  constructor() {
    // Context: dashboard vs feed (drives page-preview vs configure).
    this.store
      .pipe(select(selectCurrentObject), takeUntilDestroyed())
      .subscribe((obj) => {
        this.isDashboard = obj?.object_class_id === NomObjectClass.DASHBOARD;
      });
  }

  onSelectAllChange(selected: boolean): void {
    this.selectAllChecked = selected;
    this.selectAllChange.emit(selected);
  }

  onPagePreview(): void {
    this.was.openPagePreview();
  }
}
